import React, { memo, useCallback, useMemo, useState } from 'react';

import { Modal, Table } from '@components/common';

import { ISortBy, ITableSort } from '@interfaces/components/table';
import { IExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';
import { IDashboardRequestStatus, IDashboardRequestStatusSummary } from '@interfaces/platform/platformDashboard';

import DashboardPOByStatusTable from '../DashboardPOByStatusTable';
import DashboardPOWhiteCard from '../DashboardPOWhiteCard';
import { getAcquireAssetsColumns } from './utils';

const MODAL_WIDTH = '624px';
const MODAL_HEIGHT = '720px';
const TABLE_HEIGHT = '675px';

interface IDashboardPOStatusesTable {
	dashboardRequestStatusesSummary: IDashboardRequestStatusSummary[];
	isLoadingDashboardRequestStatusesSummary: boolean;
	sortFieldName: string | null;
	sortDirection: boolean | null;
	handleSort: (sortBy: ISortBy[]) => void;
	handleExport: (params: IExportFileDTO) => void;
	isSingleOrganization?: boolean;
}

const DashboardPOStatusesTable = ({
	dashboardRequestStatusesSummary,
	isLoadingDashboardRequestStatusesSummary,
	handleSort,
	handleExport,
	sortFieldName,
	sortDirection,
	isSingleOrganization,
}: IDashboardPOStatusesTable) => {
	const [status, setStatus] = useState<IDashboardRequestStatus | null>(null);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const openStatusTable = useCallback((status?: IDashboardRequestStatus) => {
		if (!status) return null;

		setStatus(status);
	}, []);

	const closeStatusTable = useCallback(() => setStatus(null), []);

	const columns = useMemo(
		() => getAcquireAssetsColumns({ openStatusTable, isSingleOrganization })
		, [openStatusTable, isSingleOrganization]);

	return (
		<DashboardPOWhiteCard
			handleExport={handleExport}
			isWithExport
			title='All request in statuses'
		>
			<Table
				columns={columns}
				data={dashboardRequestStatusesSummary}
				isLoading={isLoadingDashboardRequestStatusesSummary}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
				maxHeight={TABLE_HEIGHT}
				sort={sort}
			/>
			{status && (
				<Modal
					height={MODAL_HEIGHT}
					isOpen={!!status}
					isScrollableContent
					onClose={closeStatusTable}
					title='List per Organisation'
					width={MODAL_WIDTH}
				>
					<DashboardPOByStatusTable status={status} />
				</Modal>
			)}
		</DashboardPOWhiteCard>
	);
};

export default memo(DashboardPOStatusesTable);
